import {
  type MspUserDetails,
  type MspUserListItem,
  useMspUsersStore,
} from "@/_store/msp/msp-users.module.ts";
import { RouteName } from "@/constants/routes.ts";
import { type UserDetails, type UserListItem, useUsersStore } from "@/_store/users.module.ts";
import { ProtectedUsersAction } from "@/constants/users.ts";
import type { AxiosResponse } from "axios";
import type { Ref } from "vue";
import { AppContext } from "@/types.ts";
import { storeToRefs } from "pinia";
import { isMspAccessRestricted, isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { isAccessRestricted, isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";

export type PreviewUser = UserListItem | MspUserListItem;
export type PreviewUserDetails = UserDetails | MspUserDetails;

interface BasePreviewConfig<T extends PreviewUser, D extends PreviewUserDetails> {
  applySingleUserAction: (
    userId: string,
    {
      action,
      metadata,
    }: {
      action: ProtectedUsersAction;
      metadata?: Record<string, unknown>;
    }
  ) => Promise<void>;
  getSingleUserActions: (
    userId: string
  ) => Promise<AxiosResponse<{ items: ProtectedUsersAction[] }>>;
  getUserDetails: (user: T) => Promise<void>;
  undoActivityLog: (activityLogId: string) => Promise<void>;
  userDetails: Ref<D | null>;
  userDetailsLoading: Ref<boolean>;
  // Accepts either single-workspace `isAccessRestricted` or MSP variant; both have generic
  // signatures that don't unify cleanly. The bivariant `any[]` matches both without losing
  // callability at consumers (see BaseUserPreview.vue).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isAccessRestricted: (...args: any[]) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isActionRestricted: (...args: any[]) => boolean;
  ticketsPageRoute: RouteName;
  testIdPrefix: string;
  loadMoreMspUserActivityLogs?: () => Promise<void>;
}

export interface SingleWorkspaceUserPreviewConfig extends BasePreviewConfig<
  UserListItem,
  UserDetails
> {
  context: AppContext.SINGLE_WORKSPACE;
}

export interface MspUserPreviewConfig extends BasePreviewConfig<MspUserListItem, MspUserDetails> {
  context: AppContext.MSP;
}

export type UserPreviewConfig<T extends PreviewUser> = Extract<
  SingleWorkspaceUserPreviewConfig | MspUserPreviewConfig,
  { getUserDetails: (user: T) => Promise<void> }
>;

export const getSingleWorkspaceUserPreviewConfig = (): SingleWorkspaceUserPreviewConfig => {
  const usersStore = useUsersStore();
  const { userDetails, userDetailsLoading } = storeToRefs(usersStore);
  const { applySingleUserAction, getSingleUserActions, getUserDetails, undoActivityLog } =
    usersStore;
  return {
    applySingleUserAction,
    getSingleUserActions,
    getUserDetails,
    undoActivityLog,
    userDetails,
    userDetailsLoading,
    isAccessRestricted: isAccessRestricted,
    isActionRestricted: isWorkspaceFrozenOrActionRestricted,
    ticketsPageRoute: RouteName.TICKETS,
    testIdPrefix: "users-preview",
    context: AppContext.SINGLE_WORKSPACE,
  };
};

export const getMspUserPreviewConfig = (): MspUserPreviewConfig => {
  const usersStore = useMspUsersStore();
  const { userDetails, userDetailsLoading } = storeToRefs(usersStore);
  const {
    applySingleUserAction,
    getSingleUserActions,
    getUserDetails,
    undoActivityLog,
    loadMoreMspUserActivityLogs,
  } = usersStore;
  return {
    applySingleUserAction,
    getSingleUserActions,
    getUserDetails,
    undoActivityLog,
    loadMoreMspUserActivityLogs,
    userDetails,
    userDetailsLoading,
    isAccessRestricted: isMspAccessRestricted,
    isActionRestricted: isMspActionRestricted,
    ticketsPageRoute: RouteName.MSP_TICKETS_PAGE,
    testIdPrefix: "msp-users-preview",
    context: AppContext.MSP,
  };
};
